import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { actions } from '../Users/redux/actions';
import Strings from '../../../Strings';
import { DATE_MONTH_DAY_YEAR, HEADER_BUTTON_WHITE } from '../../../constants';
import './RoleDetail.scss';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { Input, SelectField } from '../../../containers/Form';
import LoadingRenderer from '../../../components/LoadingRenderer';
import { openModalAction } from '../../../actions/modal';
import { notificationActions } from '../../../components/Notification/redux/actions';
import OrgRolePermissions from '../../Organizations/Configuration/OrgRolePermissions';

function RoleDetail(props) {
  const { match, getRoleDetails, editRole, onNavigate, getRoleFormBundle, showNotification } = props;
  const roleId = match?.params?.roleId;

  const [role, setRole] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isEditingPermissions, setIsEditingPermissions] = React.useState(false);
  const [editedRole, setEditedRole] = React.useState({});
  const [archetypes, setArchetypes] = React.useState([]);
  const [submitHandler, setSubmitHandler] = React.useState(null);
  const [permissionsReloadKey, setPermissionsReloadKey] = React.useState(0);

  const handleSubmitHandlerChange = React.useCallback((handler) => {
    setSubmitHandler(() => handler);
  }, []);

  React.useEffect(() => {
    if (roleId && getRoleDetails) {
      setIsLoading(true);
      setError(null);
      getRoleDetails(roleId)
        .then(resp => {
          const roleData = resp?.response;
          if (roleData) {
            setRole(roleData);
          } else {
            setError('Role not found');
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load role:', err);
          setError('Failed to load role');
          setIsLoading(false);
        });
    }
  }, [roleId, getRoleDetails]);

  // Load form bundle (archetypes for dropdown)
  React.useEffect(() => {
    if (getRoleFormBundle) {
      getRoleFormBundle().then(resp => {
        if (resp?.response) {
          const bundle = resp.response;
          if (bundle.archetypes) {
            setArchetypes(bundle.archetypes?.map(arch => ({ ...arch, value: arch.id, label: arch.name })));
          }
        }
      });
    }
  }, [getRoleFormBundle]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // For archetype_id, convert to number
    if (name === 'archetype_id') {
      newValue = value ? parseInt(value, 10) : null;
    }

    // Only track the field if it's different from the original
    if (role[name] !== newValue) {
      setEditedRole(prev => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      // Remove from editedRole if value is back to original
      setEditedRole(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSave = async () => {
    if (!editRole || !role) return;

    // Only send if there are actual changes
    if (Object.keys(editedRole).length === 0) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await editRole(roleId, editedRole);
      // Reload role data
      const resp = await getRoleDetails(roleId);
      if (resp?.response) {
        setRole(resp.response);
      }
      showNotification('Role updated successfully');
      setIsEditing(false);
      setEditedRole({});
      setIsLoading(false);
    } catch (err) {
      showNotification('Failed to update role', 5000, true);
      console.error('Failed to update role:', err);
      setError('Failed to update role');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('/admin/roles');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = moment(dateString);
    return date.format(DATE_MONTH_DAY_YEAR);
  };

  if (error && !role) {
    return (
      <div className="role-detail-page">
        <div className="error-message">
          <p>{error}</p>
        </div>
        <button className="btn-back" onClick={handleBack}>
          {Strings.backToRoles || 'Back to Roles'}
        </button>
      </div>
    );
  }

  const displayRole = isEditing ? { ...role, ...editedRole } : role;

  return (
    <LoadingRenderer isLoading={isLoading}>
      <PageHeader
        onBackClick={() => history.back()}
        left={role.title || 'Role Details'}
      />
      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          <p>{error}</p>
        </div>
      )}
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        className="scrollbar-right-margin"
      >
        <div className="rjsf config-tab-container no-save-button">
          <fieldset>
            <div className="form-group field field-object leaf">
              <fieldset>
                <header className="header-with-button">
                  {Strings.roleInformation || 'Role Information'}
                  {isEditing ? (
                    <div className="buttons">
                      <Button
                        class={HEADER_BUTTON_WHITE}
                        onClick={() => {
                          setIsEditing(false);
                          setEditedRole({});
                        }}
                        disabled={isLoading}
                        title={Strings.cancel || 'Cancel'}
                      />
                      <Button class={HEADER_BUTTON_DARK_BLUE} onClick={handleSave} disabled={isLoading} title={isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')} />
                    </div>
                  ) : (
                    <Button
                      class={HEADER_BUTTON_DARK_BLUE}
                      onClick={() => {
                        setIsEditing(true);
                        setEditedRole({});
                      }}
                      title={Strings.editRole || 'Edit Role'}
                    />
                  )}
                </header>              
                <Input type="text" id="id" label={Strings.id || 'ID'} value={role.id || 'N/A'} readOnly />
                <Input type="text" id="role_uuid" label={Strings.roleUuid || 'Role UUID'} value={role.role_uuid || 'N/A'} readOnly />
                <Input
                  id="title"
                  type="text"
                  label={Strings.title || 'Title'}
                  value={displayRole.title || ''}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <SelectField
                  id="archetype_id"
                  label={Strings.archetype || 'Archetype'}
                  value={displayRole.archetype_id || ''}
                  data={archetypes}
                  onChange={option => handleInputChange({ target: { name: 'archetype_id', value: option ? option.value : '' } })}
                  readOnly={!isEditing}
                />
                <Input type="text" id="archetype" label={Strings.archetypeId || 'Archetype ID'} value={displayRole.archetype_id   || 'N/A'} readOnly />
              </fieldset>
            </div>

            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.organization || 'Organization'}</header>
                <Input type="text" id="org_name" label={Strings.organizationName || 'Organization Name'} value={displayRole.org_name || displayRole.organization || 'N/A'} readOnly />
                <Input type="text" id="org_uuid" label={Strings.organizationUuid || 'Organization UUID'} value={displayRole.org_uuid || 'N/A'} readOnly />
                <Input type="text" id="org_id" label={Strings.organizationId || 'Organization ID'} value={displayRole.org_id || 'N/A'} readOnly />
              </fieldset>
            </div>

            {role.permissions && (
              <div className="form-group field field-object">
                <fieldset>
                  <header className="header-with-button">
                    {Strings.permissions || 'Permissions'}
                    {isEditingPermissions ? (
                      <div className="buttons">
                        <Button
                          class={HEADER_BUTTON_WHITE}
                          onClick={() => {
                            setIsEditingPermissions(false);
                            setPermissionsReloadKey(prev => prev + 1);
                          }}
                          disabled={isLoading}
                          title={Strings.cancel || 'Cancel'}
                        />
                        <Button
                          class={HEADER_BUTTON_DARK_BLUE}
                          onClick={() => {
                            if (submitHandler) {
                              submitHandler();
                              setIsEditingPermissions(false);
                            }
                          }}
                          disabled={isLoading || !submitHandler}
                          title={isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')}
                        />
                      </div>
                    ) : (
                      <Button
                        class={HEADER_BUTTON_DARK_BLUE}
                        onClick={() => {
                          setIsEditingPermissions(true);
                        }}
                        title={Strings.editPermissions || 'Edit Permissions'}
                      />
                    )}
                  </header>
                  <OrgRolePermissions
                    key={permissionsReloadKey}
                    roleId={role.role_uuid}
                    organizationId={role.org_uuid}
                    isTemplate={false}
                    readOnly={!isEditingPermissions}
                    onExternalSubmit={handleSubmitHandlerChange}
                  />
                </fieldset>
              </div>
            )}
          </fieldset>
        </div>
      </OverlayScrollbarsComponent>
    </LoadingRenderer>
  );
}

const mapDispatchToProps = dispatch => ({
  getRoleDetails: roleId => dispatch(actions.getRoleDetails(roleId)),
  editRole: (roleId, data) => dispatch(actions.editRole(roleId, data)),
  getRoleFormBundle: () => dispatch(actions.getRoleFormBundle()),
  onNavigate: path => dispatch(push(path)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(RoleDetail);
